import { MosaicTile, SmoothedFrame, IntentFrame } from '../architecture/mosaic_pipeline';
import {
  GestureSubstrate,
  ConditionEvaluator,
  StateType,
} from '../architecture/universal_gesture_substrate';
import {
  FsmState,
  StateIdle,
  StateIdleCoast,
  StateReady,
  StateReadyCoast,
  StateCommit,
  StateCommitCoast,
} from '../architecture/types';

function makeGestureEvaluator(targetGesture: string): ConditionEvaluator {
  return {
    conditionId: `gesture:${targetGesture}`,
    evaluate(frame: SmoothedFrame): number {
      return frame.gestureLabel === targetGesture ? frame.confidence : 0;
    },
  };
}

function makePointerUpBioEvaluator(): ConditionEvaluator {
  return {
    conditionId: "gesture:pointer_up:bio",
    evaluate(frame: SmoothedFrame): number {
      if (frame.gestureLabel !== "pointer_up") return 0;
      // We don't have isBioPinch in SmoothedFrame, so we'll just use confidence
      return frame.confidence;
    },
  };
}

export class GestureFSMTile implements MosaicTile<SmoothedFrame, IntentFrame> {
  public readonly tileId = 'GestureFSMTile';
  private readonly _substrate: GestureSubstrate;

  private _stateCache: FsmState = new StateIdle();

  constructor(private readonly config: Readonly<{ fsm_conf_high: number; fsm_conf_low: number; coast_timeout_ms: number }>) {
    this._substrate = new GestureSubstrate({
      coastTimeoutMs: config.coast_timeout_ms,
      coastConfLow: config.fsm_conf_low,
      coastConfHigh: config.fsm_conf_high,
    });
    this._registerBuiltinRules();
  }

  private _registerBuiltinRules(): void {
    const h = this.config.fsm_conf_high;
    const l = this.config.fsm_conf_low;

    this._substrate.register({
      ruleId: "idle_to_ready",
      fromState: "IDLE",
      toState: "READY",
      evaluator: makeGestureEvaluator("open_palm"),
      conf_high: h,
      conf_low: l,
      dwell_ms: 100,
      priority: 0,
    });

    this._substrate.register({
      ruleId: "idle_reinforce_fist",
      fromState: "IDLE",
      toState: "IDLE",
      evaluator: makeGestureEvaluator("closed_fist"),
      conf_high: h,
      conf_low: l,
      dwell_ms: 0,
      priority: 5,
      inhibits: ["idle_to_ready"],
    });

    this._substrate.register({
      ruleId: "ready_to_idle",
      fromState: "READY",
      toState: "IDLE",
      evaluator: makeGestureEvaluator("closed_fist"),
      conf_high: h,
      conf_low: l,
      dwell_ms: 0,
      priority: 10,
      inhibits: ["ready_to_commit"],
    });

    this._substrate.register({
      ruleId: "ready_to_commit",
      fromState: "READY",
      toState: "COMMIT_POINTER",
      evaluator: makePointerUpBioEvaluator(),
      conf_high: h,
      conf_low: l,
      dwell_ms: 100,
      priority: 0,
      inhibits: ["ready_to_idle"],
    });

    this._substrate.register({
      ruleId: "commit_to_ready",
      fromState: "COMMIT_POINTER",
      toState: "READY",
      evaluator: makeGestureEvaluator("open_palm"),
      conf_high: h,
      conf_low: l,
      dwell_ms: 100,
      priority: 2,
      inhibits: ["commit_to_idle"],
    });

    this._substrate.register({
      ruleId: "commit_to_idle",
      fromState: "COMMIT_POINTER",
      toState: "IDLE",
      evaluator: makeGestureEvaluator("closed_fist"),
      conf_high: h,
      conf_low: l,
      dwell_ms: 100,
      priority: 1,
      inhibits: ["commit_to_ready"],
    });
  }

  public process(frame: SmoothedFrame): IntentFrame | null {
    this._substrate.processFrame(frame);
    
    const t = this._substrate.state;
    if (this._stateCache.type !== t) {
      this._stateCache = this._makeState(t);
    }
    
    let intentState: 'IDLE' | 'READY' | 'COMMIT_POINTER' | 'COAST' = 'IDLE';
    if (t === 'IDLE') intentState = 'IDLE';
    else if (t === 'READY') intentState = 'READY';
    else if (t === 'COMMIT_POINTER') intentState = 'COMMIT_POINTER';
    else intentState = 'COAST';

    return {
      ...frame,
      intentState,
      isPinching: intentState === 'COMMIT_POINTER'
    };
  }

  private _makeState(t: StateType): FsmState {
    switch (t) {
      case "IDLE": return new StateIdle();
      case "IDLE_COAST": return new StateIdleCoast();
      case "READY": return new StateReady();
      case "READY_COAST": return new StateReadyCoast();
      case "COMMIT_POINTER": return new StateCommit();
      case "COMMIT_COAST": return new StateCommitCoast();
    }
  }
}
